import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../context/CompanyContext';
import { INDIA_STATES } from '../constants/indiaStates';
import GstinLookup from '../components/GstinLookup';
import api from '../api';
import './Dashboard.css';
import './Setup.css';

const EMPTY_FORM = {
  name: '',
  company_type: 'pvt_ltd',
  gstin: '',
  pan: '',
  cin: '',
  state: '',
  fy_end: 'March',
};

function Setup() {
  const navigate = useNavigate();
  const { refreshCompanies, setSelectedCompanyId } = useCompany();
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const showCin = form.company_type === 'pvt_ltd' || form.company_type === 'llp';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    setSubmitting(true);
    setError(null);

    api
      .post('/companies', {
        name: form.name,
        company_type: form.company_type,
        gstin: form.gstin || null,
        pan: form.pan || null,
        cin: showCin ? form.cin || null : null,
        state: form.state || null,
      })
      .then((res) => {
        return refreshCompanies().then(() => {
          setSelectedCompanyId(res.data.id);
          navigate('/dashboard', {
            replace: true,
            state: { toast: 'Company setup complete! Your compliance calendar is ready.' },
          });
        });
      })
      .catch((err) => setError(err.message))
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="setup-page">
      <div className="setup-card">
        <div className="setup-brand">
          KROSI<span>FIN</span>
        </div>
        <div className="setup-brand-sub">by NeedCFO</div>
        <h1 className="setup-title">Welcome to Krosifin</h1>
        <p className="setup-subtitle">Set up your company profile to get started</p>

        {error && <p className="dash-error">{error}</p>}

        <form onSubmit={handleSubmit}>
          <div className="field-group">
            <span className="field-label">Company Name *</span>
            <input
              className="field-input"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>

          <div className="field-group">
            <span className="field-label">Company Type</span>
            <select
              className="field-select"
              value={form.company_type}
              onChange={(e) => setForm({ ...form, company_type: e.target.value })}
            >
              <option value="pvt_ltd">Private Limited</option>
              <option value="llp">LLP</option>
              <option value="proprietorship">Proprietorship</option>
              <option value="partnership">Partnership</option>
            </select>
          </div>

          <div className="field-row">
            <div className="field-group">
              <span className="field-label">GSTIN</span>
              <input
                className="field-input"
                value={form.gstin}
                onChange={(e) => setForm({ ...form, gstin: e.target.value })}
              />
              <GstinLookup
                gstin={form.gstin}
                variant="link"
                onFetched={(data) =>
                  setForm((f) => ({
                    ...f,
                    name: data.legal_name || data.trade_name || f.name,
                    state: data.state || f.state,
                  }))
                }
              />
            </div>
            <div className="field-group">
              <span className="field-label">PAN</span>
              <input
                className="field-input"
                value={form.pan}
                onChange={(e) => setForm({ ...form, pan: e.target.value })}
              />
            </div>
          </div>

          {showCin && (
            <div className="field-group">
              <span className="field-label">CIN</span>
              <input
                className="field-input"
                value={form.cin}
                onChange={(e) => setForm({ ...form, cin: e.target.value })}
              />
            </div>
          )}

          <div className="field-row">
            <div className="field-group">
              <span className="field-label">State</span>
              <select
                className="field-select"
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
              >
                <option value="">Select state</option>
                {INDIA_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="field-group">
              <span className="field-label">Financial Year End</span>
              <select
                className="field-select"
                value={form.fy_end}
                onChange={(e) => setForm({ ...form, fy_end: e.target.value })}
              >
                <option value="March">March</option>
                <option value="December">December</option>
              </select>
            </div>
          </div>

          <div className="dash-btn-row">
            <button className="dash-btn dash-btn-orange" type="submit" disabled={submitting}>
              {submitting ? 'Setting up...' : 'Create Company & Continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Setup;
